import { useCallback, useEffect, useRef } from 'react'
import { Spin, notification } from 'antd'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useForm } from '@tanstack/react-form'
import { getRobotById, updateRobot } from '@/api/robots.api'
import { fetchFloorplans } from '@/api/floorplans.api'
import type { Robot } from '@/types/api'

import { Button } from '@/components/ui/button'
import { Drawer, DrawerFooter } from '@/components/ui/drawer'
import { FormField } from '@/components/ui/form-field'
import { Input } from '@/components/ui/input'
import { Select } from '@/components/ui/select'
import type { ChangeEvent } from 'react'
import { useAtomValue } from 'jotai'
import { currentUserAtom } from '@/store/auth.atom'
import { fetchUnits } from '@/api/units.api'

interface EditRobotFormValues {
  code: string
  name: string
  unitId: string
  diagramId: string
  diagramName?: string
}

export function DrawerEditRobot({
  visible,
  robotId,
  onClose,
  onSuccess,
}: {
  visible: boolean
  robotId: string | undefined
  onClose: () => void
  onSuccess?: () => void
}) {
  const queryClient = useQueryClient()
  const currentUser = useAtomValue(currentUserAtom)
  const showUnitColumn = currentUser?.userType === 1

  const form = useForm({
    defaultValues: {
      code: '',
      name: '',
      unitId: '',
      diagramId: '',
      unitName: '',
      diagramName: '',
    },
    onSubmit: async ({ value }) => {
      mutate(value)
    },
  })

  const isInitialized = useRef(false)
  const {
    data: robot,
    isLoading,
    error,
    refetch,
  } = useQuery<Robot | undefined, Error>({
    queryKey: ['robot', robotId],
    queryFn: ({ signal }) => getRobotById(robotId!, signal),
    enabled: visible && !!robotId,
    refetchOnMount: 'always',
    staleTime: 0,
    gcTime: 0,
  })

  useEffect(() => {
    if (visible && robot && !isInitialized.current) {
      form.setFieldValue('code', robot.code || '')
      form.setFieldValue('name', robot.name || '')
      form.setFieldValue('unitId', robot.unitResponse?.id || '')
      form.setFieldValue('unitName', robot.unitResponse?.name || '')
      form.setFieldValue('diagramId', robot.diagramInfo?.id || '')
      form.setFieldValue('diagramName', robot.diagramInfo?.name || '')
      isInitialized.current = true
    }
  }, [visible, robot, form])

  useEffect(() => {
    if (!visible) {
      isInitialized.current = false
      form.reset()
    }
  }, [visible, form])

  // Mutation xử lý cập nhật Robot
  const { mutate, isPending } = useMutation({
    mutationFn: async (values: EditRobotFormValues) => {
      const payload = {
        name: values.name,
        status: robot?.status ?? 1,
        unitId: showUnitColumn ? values.unitId : currentUser?.unitId,
        diagramId: values.diagramId,
      }
      return updateRobot(robotId!, payload)
    },
    onSuccess: (res) => {
      notification.success({
        title:
          res?.responseMessage || res?.message || 'Cập nhật robot thành công!',
      })
      queryClient.invalidateQueries({ queryKey: ['robots'] })
      onSuccess?.()
      onClose()
    },
  })

  const loadUnits = useCallback(async ({ page, signal, searchText }: any) => {
    const res = await fetchUnits(
      page,
      10,
      { keySearch: searchText?.trim() || undefined, status: 1 },
      signal,
    )
    const arr = Array.isArray(res.items) ? res.items : []
    const pageSize = 10
    const hasNextPage = arr.length >= pageSize

    return {
      options: arr.map((u: any) => ({
        value: u.id,
        label: u.name,
      })),
      hasNextPage,
      nextPage: hasNextPage ? page + 1 : undefined,
    }
  }, [])

  return (
    <Drawer
      open={visible}
      onClose={onClose}
      title="CẬP NHẬT ROBOT"
      styles={{ body: { overflow: 'auto', padding: '24px' } }}
      footer={
        <DrawerFooter>
          <Button
            variant="outline"
            size="md"
            onClick={onClose}
            className="min-w-[160px] h-10"
          >
            Hủy
          </Button>
          <Button
            variant="primary"
            size="md"
            className="min-w-[160px] h-10"
            onClick={() => form.handleSubmit()}
            disabled={isPending}
          >
            Lưu
          </Button>
        </DrawerFooter>
      }
    >
      {isLoading ? (
        <div className="flex h-full items-center justify-center p-12">
          <Spin size="large" />
        </div>
      ) : error ? (
        <div className="text-center p-8 text-red-500">
          Không thể tải dữ liệu robot.
          <br />
          <Button variant="outline" onClick={() => refetch()} className="mt-4">
            Thử lại
          </Button>
        </div>
      ) : (
        <form
          onSubmit={(e) => {
            e.preventDefault()
            e.stopPropagation()
            form.handleSubmit()
          }}
          className="pt-2 flex flex-col gap-5"
        >
          {/* Tên robot */}
          <form.Field
            name="name"
            validators={{
              onChange: ({ value }: { value: string }) =>
                !value ? 'Bắt buộc' : undefined,
            }}
          >
            {(field) => (
              <FormField
                field={field}
                label={<span className="font-medium">Tên robot</span>}
                required
              >
                <Input
                  placeholder="Nhập tên robot"
                  maxLength={50}
                  size="large"
                  value={field.state.value}
                  onChange={(e: ChangeEvent<HTMLInputElement>) =>
                    field.handleChange(e.target.value)
                  }
                  onBlur={field.handleBlur}
                  disabled={isPending}
                />
              </FormField>
            )}
          </form.Field>

          {/* Đơn vị (Chỉ hiển thị cho Admin quản trị) */}
          {showUnitColumn ? (
            <form.Field
              name="unitId"
              validators={{
                onChange: ({ value }: { value: string }) =>
                  !value ? 'Vui lòng chọn đơn vị' : undefined,
              }}
            >
              {(field) => (
                <FormField
                  field={field}
                  label={<span className="font-medium">Đơn vị</span>}
                  required
                >
                  <Select
                    cacheKey="robot-edit-units"
                    initialPage={0}
                    lazy
                    size="large"
                    placeholder="Chọn đơn vị"
                    allowClear
                    className="w-full h-10"
                    enableRemoteSearch
                    value={field.state.value}
                    loadOptions={loadUnits}
                    initialOptions={
                      form.getFieldValue('unitName')
                        ? [
                            {
                              value: form.getFieldValue('unitId'),
                              label: form.getFieldValue('unitName'),
                            },
                          ]
                        : []
                    }
                    onLazyValueChange={(val, option) => {
                      field.handleChange(() => val as string)
                      form.setFieldValue('unitName', option?.label || '')
                      form.setFieldValue('diagramId', '')
                      form.setFieldValue('diagramName', '')
                    }}
                  />
                </FormField>
              )}
            </form.Field>
          ) : (
            ''
          )}

          {/* Dùng Subscribe để theo dõi chính xác unitId đang chọn của form và fetch sơ đồ tương ứng */}
          <form.Subscribe selector={(state) => state.values.unitId}>
            {(formUnitId) => (
              <FloorplanSelectField
                form={form}
                currentUnitId={
                  showUnitColumn ? formUnitId : currentUser?.unitId
                }
              />
            )}
          </form.Subscribe>
        </form>
      )}
    </Drawer>
  )
}

function FloorplanSelectField({
  form,
  currentUnitId,
}: {
  form: any
  currentUnitId: string | undefined
}) {
  const diagramId = form.getFieldValue('diagramId')
  const diagramName = form.getFieldValue('diagramName')
  return (
    <form.Field
      name="diagramId"
      validators={{
        onChange: ({ value }: { value: string }) =>
          !value ? 'Vui lòng chọn sơ đồ' : undefined,
      }}
    >
      {(field: any) => (
        <FormField
          field={field}
          label={<span className="font-medium">Sơ đồ</span>}
          required
        >
          <Select
            key={diagramId || 'empty'}
            lazy
            size="large"
            cacheKey={`floorplan-select-${currentUnitId}`}
            placeholder="Chọn sơ đồ"
            className="w-full h-10"
            value={field.state.value}
            enableRemoteSearch
            initialOptions={
              diagramId && diagramName
                ? [
                    {
                      value: diagramId,
                      label: diagramName,
                    },
                  ]
                : []
            }
            loadOptions={async ({ page, signal, searchText }) => {
              if (!currentUnitId) return { options: [] }

              const res = await fetchFloorplans(
                page,
                10,
                {
                  unitId: currentUnitId || undefined,
                  keySearch: searchText?.trim() || undefined,
                },
                signal,
              )

              const arr = Array.isArray(res.items) ? res.items : []
              const hasNextPage = arr.length >= 10

              return {
                options: arr.map((f: any) => ({
                  value: f.id,
                  label: f.name,
                })),
                hasNextPage,
                nextPage: hasNextPage ? page + 1 : undefined,
              }
            }}
            onLazyValueChange={(val, option) => {
              field.handleChange(val)
              form.setFieldValue('diagramName', option?.label || '')
            }}
          />
        </FormField>
      )}
    </form.Field>
  )
}
